import { EditorProps } from "@grapesjs/react";
import grapesjs, { Editor, EditorConfig } from "grapesjs";
import { useEffect, useRef, useState } from "react";

import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import swal from "sweetalert";
import CustomDesignedEditor from "../../components/GrapesJS/CustomDesignedEditor";
import ImagePopUpV3 from "../../components/ImagePopUpV3";
import { Page } from "../../features/addPages/addPages";
import { setGlobalPopupElement, setLoading } from "../../features/popupSlice";
import {
	fetchImagesModule,
	getPageDetails,
	updateAllPages,
} from "../../modules/mediaManager";
import { CustomComponentSettingsFunction } from "../../settings";
import { getImageUrl } from "../../utils";
import { showToast } from "../HomePage";
import { customLandingWidgets } from "../../utils/customLandingWidgets";
import landingPages from "../../utils/landingPages";

declare global {
	interface Window {
		grapesjs: typeof grapesjs;
	}
}

export interface ExtraEditorProps {
	publishPage?: () => void;
	exportJson?: () => void;
}

export const defaultOptions: EditorConfig = {
	storageManager: false,
	height: "100vh",
	assetManager: {
		// assets: [],
		// custom: true,
		upload: false,
		autoAdd: false,
	},
	undoManager: {
		trackSelection: false,
	},
	selectorManager: {
		componentFirst: true,
	},
	style: landingPages.styles.content,
	// components: landingPages.landingPage1.content,
};

const initialData = {
	components: [],
	// style: [
	// 	{
	// 		selectors: ["#ih4y"],
	// 		style: {
	// 			padding: "10px",
	// 		},
	// 	},
	// ],
};

// Simulate an asynchronous API call with pagination
const fetchAssets = async (page: number, limit = 10): Promise<any[]> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await fetchImagesModule({
				currentPage: page,
				lastPage: 10,
				total: 100,
			});

			resolve(res?.data || []);
		} catch (error) {
			reject(error);
		}
	});
};

export const plugins: EditorProps["plugins"] = [
	{
		id: "gjs-blocks-basic",
		src: "https://unpkg.com/grapesjs-blocks-basic",
	},
	"grapesjs-plugin-forms",
	"grapesjs-component-countdown",
	// {
	// 	id: "grapesjs-preset-webpage",
	// 	src: "https://unpkg.com/grapesjs-preset-webpage",
	// },
];

const widgetName = "custom-page";

const CustomLandingPage = () => {
	const dispatch = useDispatch();
	const { pageTitle: pageId } = useParams();

	const [page, setPage] = useState<Page | null>(null);
	const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
	const [editorSettings, setEditorSettings] = useState<{
		[key: string]: any;
	}>({});

	const [currentPage, setCurrentPage] = useState(0);
	const [isFetching, setIsFetching] = useState(false);
	const [assets, setAssets] = useState<any[]>([]);
	// const [selectedComponent, setSelectedComponent] = useState<any>(null);
	const selectedComponent = useRef<any>(null);

	useEffect(() => {
		if (pageId && editorInstance) {
			dispatch(setLoading(true));

			getPageDetails(pageId)
				.then((res) => {
					setPage(res.data);

					const widget = res.data?.widgets?.find(
						(widget: any) => widget.name === widgetName
					);

					if (widget && editorInstance) {
						const data = widget.data;

						editorInstance.setComponents(data.components);
						editorInstance.setStyle(data.style);
						console.log("data.style :>> ", data.style);
					}
				})
				.finally(() => {
					dispatch(setLoading(false));
				});
		}
	}, [pageId, dispatch, editorInstance]);

	const onEditor = (editor: Editor) => {
		// editor.setComponents(initialData.components);
		// editor.setStyle(initialData.style);

		const addCustomWidget = (
			editor: Editor,
			data: {
				className: string;
				content: string;
				label: string;
				media: string;
				category: string;
				customSettings?: CustomComponentSettingsFunction;
			}
		) => {
			data.customSettings?.(
				editor,
				data.className,
				data.content,
				{
					data: editorSettings,
					setData: setEditorSettings,
				},
				{
					dispatch: dispatch,
				}
			);

			// const cssComposer = editor.CssComposer;
			// const sm = editor.SelectorManager;
			// var c1 = sm.add(data.className);

			// const rule = cssComposer.add([c1]);

			// rule.set("style", {});

			editor.BlockManager.add(data.className, {
				label: data.label,
				media: data.media,
				content: data.content,
				// content: {
				// 	type: data.className, // Directly specify the custom component type
				// 	components: {
				// 		type: "html",
				// 		content: data.content,
				// 	},
				// },
				category: data.category,
				// attributes: {
				// 	class: data.className,
				// 	title: data.label,
				// 	input: "text",
				// },
			});
		};

		customLandingWidgets.forEach((widget) => {
			addCustomWidget(editor, {
				category: "Sections",
				className: widget.key,
				content: widget.comp,
				label: widget.name,
				media: widget.media || widget.comp,
				customSettings: widget.customComponentSettings,
			});
		});

		// Listen for component selection events
		editor.on("component:selected", (model) => {
			const type = model.get("type");
			console.log("model, type :>> ", model, type);
			if (type === "image") {
				// setSelectedComponent(model); // Set the selected image component
				selectedComponent.current = model;
			} else {
				// setSelectedComponent(null);
				selectedComponent.current = null;
			}
		});

		// Add an anchor button component with traits for colors and href
		editor.DomComponents.addType("anchor-button", {
			model: {
				defaults: {
					tagName: "a",
					attributes: {
						href: "#",
						style: "display: inline-block; padding: 10px 20px; text-decoration: none; color: white; background-color: blue; border-radius: 4px; text-align: center;",
					},
					content: "Click Me",
					traits: [
						{
							type: "text",
							label: "Href",
							name: "href",
							changeProp: true,
						},
						{
							type: "color",
							label: "Text Color",
							name: "color",
							changeProp: true,
						},
						{
							type: "color",
							label: "Background Color",
							name: "background-color",
							changeProp: true,
						},
					],
				},
			},
			view: {
				onRender() {
					const model = this.model;
					// Update styles dynamically based on traits
					model.on("change:color", () => {
						this.el.style.color = model.get("color");
					});
					model.on("change:background-color", () => {
						this.el.style.backgroundColor =
							model.get("background-color");
					});
					model.on("change:href", () => {
						(this.el as HTMLAnchorElement).href = model.get("href");
					});
				},
			},
		});

		// Add the anchor button to the block manager
		editor.BlockManager.add("anchor-button", {
			label: "Anchor Button",
			category: "Basic",
			attributes: { class: "fa fa-link" },
			media: "<i class='fa fa-link'></i>",
			content: {
				type: "anchor-button",
			},
		});

		// Override the default asset manager command
		editor.Commands.add("open-assets", {
			run(editor, sender) {
				console.log("editor open-assets :>> ", editor);
				dispatch(
					setGlobalPopupElement(
						<ImagePopUpV3
							onClose={() =>
								dispatch(setGlobalPopupElement(null))
							}
							onUpdate={(images) => {
								console.log(
									"images :>> ",
									images,
									selectedComponent
								);
								if (selectedComponent) {
									// Update the 'src' attribute of the selected image component
									// selectedComponent.set("attributes", {
									// 	...selectedComponent.get("attributes"),
									// 	src: images?.[0],
									// });

									// Update the 'src' attribute of the selected image component
									selectedComponent.current?.set(
										"attributes",
										{
											...selectedComponent.current.get(
												"attributes"
											),
											src: getImageUrl(
												images?.[0]?.path || ""
											),
										}
									);

									console.log(
										"selectedComponent :>> ",
										selectedComponent
									);
								}
								dispatch(setGlobalPopupElement(null));
							}}
						/>
					)
				);
				// setShowImagePopup(true); // Show the custom ImagePopup
				try {
					sender?.set("active", false); // Turn off the default asset manager behavior
				} catch {}
			},
		});

		setEditorInstance(editor); // Save the editor instance
	};

	// Function to load more assets for the asset manager
	const loadMoreAssets = async (callback: any, page = currentPage + 1) => {
		try {
			// Fetch the next page of assets asynchronously
			const res = await fetchAssets(page);
			const newAssets = res
				?.filter((img) => !!img?.path)
				?.map((image) => getImageUrl(image.path));
			console.log("newAssets :>> ", newAssets);

			// Add the new assets to the existing assets array
			setAssets((prevAssets) => [...prevAssets, ...newAssets]);

			// Trigger the callback to update GrapesJS asset manager
			callback(newAssets);

			// Update the current page and reset fetching state
			setCurrentPage(page);
			setIsFetching(false);
		} catch (error) {
			console.error(error);
		}
	};

	// Function to export JSON
	const exportJson = () => {
		if (editorInstance) {
			const components = editorInstance.getComponents();
			const style = editorInstance.getStyle();

			const exportComponentData = (component: any) => {
				const componentData = component.toJSON(); // Get the default JSON data of the component

				// Include the tagName in the exported data
				componentData.tagName = component.get("tagName");

				// Recursively process child components
				if (component.components) {
					componentData.components = component
						.components()
						.map((child: any) => exportComponentData(child));
				}

				return componentData;
			};

			// Process each top-level component and recursively add tagName
			const exportedComponents = components.map((component) =>
				exportComponentData(component)
			);

			const exportedData = {
				components: exportedComponents,
				style: style,
			};

			// Convert the JSON to a readable string
			const jsonString = JSON.stringify(exportedData, null, 2);
			console.log("Exported JSON:", jsonString);

			// Optionally download the JSON as a file
			const blob = new Blob([jsonString], { type: "application/json" });
			const link = document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download = "grapesjs-data.json";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	const patchPageData = (status: "draft" | "published", data: any) => {
		const widgets = [
			{
				name: widgetName,
				title: widgetName,
				active: true,
				data: data,
			},
		];

		const handleUpdatePage = (screenMode?: string) => {
			const newData: any = {
				...page,
				status: status,
				widgets: widgets,
				// settings: pageSettings,
				...(screenMode && { screenMode: screenMode }),
			};

			updateAllPages(newData, widgets, "widgets")
				.then((res) => {
					// dispatch(fetchAllPages())
					setPage(res.data);
					if (status === "published") {
						swal("Good job!", "Page published", "success");
					} else {
						showToast("Added and saved as draft");
					}
				})
				.catch(() => {
					swal("Something went wrong!", "", "error");
				});
		};

		if (page?.is_front_page && status === "published") {
			handleUpdatePage("all");
		} else {
			handleUpdatePage();
		}
	};

	const publishPage = () => {
		if (editorInstance) {
			const components = editorInstance.getComponents();
			const style = editorInstance.getCss();

			const exportComponentData = (component: any) => {
				const componentData = component.toJSON(); // Get the default JSON data of the component

				// Include the tagName in the exported data
				componentData.tagName = component.get("tagName");

				// Recursively process child components
				if (component.components) {
					componentData.components = component
						.components()
						.map((child: any) => exportComponentData(child));
				}

				return componentData;
			};

			// Process each top-level component and recursively add tagName
			const exportedComponents = components.map((component) =>
				exportComponentData(component)
			);

			const exportedData = {
				components: exportedComponents,
				style: style,
			};

			// Convert the JSON to a readable string
			const jsonString = JSON.stringify(exportedData, null, 2);
			console.log("Exported JSON:", jsonString, components, style);

			if (page) {
				patchPageData("published", exportedData);
			}
		}
	};

	const defaultEditorProps: EditorProps = {
		// grapesjs: window.grapesjs,
		grapesjs: grapesjs,
		// grapesjsCss: 'http://localhost:8080/dist/css/grapes.min.css',
		grapesjsCss: "https://unpkg.com/grapesjs/dist/css/grapes.min.css",
		plugins: plugins,
		options: defaultOptions,
		onChange: (props) => {
			console.log("Changes", props);
		},
		onUpdate: (props) => {
			console.log("Update", props);
		},
	};

	return (
		<CustomDesignedEditor
			{...defaultEditorProps}
			onEditor={onEditor}
			publishPage={publishPage}
			exportJson={exportJson}
		/>
	);

	// return (
	// 	<div>
	// 		<button className="btn !w-auto mb-2 px-3 mr-3" onClick={exportJson}>
	// 			Export as JSON
	// 		</button>
	// 		<button className="btn !w-auto mb-2 px-3" onClick={publishPage}>
	// 			Publish
	// 		</button>
	// 		{/* <DefaultEditor {...defaultEditorProps} onEditor={onEditor} /> */}

	// 		<CustomDesignedEditor {...defaultEditorProps} onEditor={onEditor} />
	// 	</div>
	// );
};

export default CustomLandingPage;
